import React from 'react';
import { AuthHelper } from '../../types/auth-helper-types';
import { LocalStorage } from './local-storage';
import { reducer } from './reducer';
import { isUnauthorized } from './helpers';
import { useRefreshToken } from './hooks';
import { RefreshTokenFn, AuthContextState } from './types';

const StateContext = React.createContext<AuthContextState | null>(null);
const DispatchContext = React.createContext<AuthContextDispatch | null>(null);

type AuthContextDispatch = AuthHelper;

function useAuthState(): AuthContextState {
    const context = React.useContext(StateContext);
    if (context === null) {
        throw new Error('useAuthState must be used within an AuthContextProvider');
    }
    return context;
}

function useAuthHelper(): AuthHelper {
    const context = React.useContext(DispatchContext);
    if (context === null) {
        throw new Error('useAuthHelper must be used within an AuthContextProvider');
    }
    return context;
}
// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
type HttpResponse<T extends unknown> = {
    status: number;
    response: T;
};

type Props = {
    children: React.ReactNode;
    numberOfSecondsBeforeExpirationToRefreshToken?: number;
    checkTokenExpirationFrequencyMs?: number;
    refreshTokenFn: RefreshTokenFn;
};

const AuthContextProvider: React.FC<Props> = (props: Props) => {
    // The presence of an email address indicates a previously successful authentication.
    // This will allow a user to get to a "private route". If the token has expired they'll
    // end up getting redirected to the login anyway, after an API call fails.
    const persistedAuthData = LocalStorage.readAuthData();
    const initialContextState =
        persistedAuthData.userId && persistedAuthData.email
            ? {
                  userId: persistedAuthData.userId,
                  email: persistedAuthData.email,
                  rememberMeDetails: {
                      email: persistedAuthData.rememberMeData.user,
                      rememberMe: persistedAuthData.rememberMeData.rememberMe,
                  },
              }
            : {
                  userId: undefined,
                  email: undefined,
                  rememberMeDetails: {
                      email: persistedAuthData.rememberMeData.user,
                      rememberMe: persistedAuthData.rememberMeData.rememberMe,
                  },
              };

    const [state, dispatch] = React.useReducer(reducer, initialContextState);
    const [tokenExpirationDate, setTokenExpirationDate] = React.useState<Date | null>(
        persistedAuthData.initialExpirationDate
    );

    const onUserNotAuthenticated = React.useCallback((): void => {
        LocalStorage.clearAuthCredentials();
        LocalStorage.clearSelectedOrganization();
        setTokenExpirationDate(null);

        if (!persistedAuthData.rememberMeData.rememberMe) {
            LocalStorage.clearRememberMeData();
            dispatch({
                type: 'userNotAuthenticated',
            });
        } else {
            dispatch({
                type: 'userNotAuthenticatedWithRememberMe',
                payload: {
                    email: persistedAuthData.rememberMeData.user,
                    rememberMe: persistedAuthData.rememberMeData.rememberMe,
                },
            });
        }
    }, [dispatch, persistedAuthData.rememberMeData]);

    const refreshTokenFn = props.refreshTokenFn;

    const memoizedAuthHelper = React.useMemo(() => {
        const authHelper: AuthHelper = {
            async send<T>(request: Promise<HttpResponse<T>>): Promise<HttpResponse<T>> {
                const initialResponse = await request;
                if (!isUnauthorized(initialResponse)) {
                    return initialResponse;
                }

                // Not authorized, so try to refresh the token
                const refreshResult = await refreshTokenFn(
                    { client_type: 'Web' },
                    { authHelper: undefined }
                );

                if (refreshResult.ok) {
                    setTokenExpirationDate(new Date(refreshResult.response.data.expiration_date));

                    // Able to refresh, so try again!
                    return request;
                }

                // Still not authorized, logout
                onUserNotAuthenticated();

                return initialResponse;
            },
            onUserAuthenticated(onUserAuthenticatedArgs: {
                email: string;
                userId: string;
                expirationDate: Date;
                rememberMe?: boolean;
            }) {
                LocalStorage.saveAuthCredentials(onUserAuthenticatedArgs.email, onUserAuthenticatedArgs.userId);
                LocalStorage.saveRememberMeData(onUserAuthenticatedArgs.email, onUserAuthenticatedArgs.rememberMe ?? true);
                setTokenExpirationDate(onUserAuthenticatedArgs.expirationDate);
                dispatch({
                    type: 'userAuthenticated',
                    payload: {
                        email: onUserAuthenticatedArgs.email,
                        userId: onUserAuthenticatedArgs.userId,
                        rememberMe: onUserAuthenticatedArgs.rememberMe ?? true,
                    },
                });
            },
            onUserNotAuthenticated,
        };
        return authHelper;
    }, [dispatch, state.email, onUserNotAuthenticated, refreshTokenFn]);

    // This handles auto-refreshing the token if the page is open
    useRefreshToken({
        email: state.email,
        tokenExpirationDate,
        onUserNotAuthenticated,
        setTokenExpirationDate,
        refreshTokenFn: props.refreshTokenFn,
        numberOfSecondsBeforeExpirationToRefreshToken: props.numberOfSecondsBeforeExpirationToRefreshToken,
        checkTokenExpirationFrequencyMs: props.checkTokenExpirationFrequencyMs,
    });

    return (
        <StateContext.Provider value={state}>
            <DispatchContext.Provider value={memoizedAuthHelper}>{props.children}</DispatchContext.Provider>
        </StateContext.Provider>
    );
};

const TestAuthContextProvider: React.FC<{ value?: AuthContextState }> = (props) => {
    const memoizedAuthHelper: AuthHelper = React.useMemo(
        () => ({
            send<T>(request: Promise<HttpResponse<T>>): Promise<HttpResponse<T>> {
                return request;
            },
            // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-empty-function, no-empty-function
            onUserAuthenticated: (args: { email: string; userId: string; expirationDate: Date }): void => {},
            // eslint-disable-next-line no-empty-function, @typescript-eslint/no-empty-function
            onUserNotAuthenticated: (): void => {},
        }),
        []
    );

    return (
        <StateContext.Provider value={props.value || { userId: undefined, email: undefined, rememberMeDetails: {} }}>
            <DispatchContext.Provider value={memoizedAuthHelper}>{props.children}</DispatchContext.Provider>
        </StateContext.Provider>
    );
};

const isAuthenticated = (state: AuthContextState): boolean => Boolean(state.email && state.userId);
const getUserId = (state: AuthContextState): string | undefined => state.userId;
const getRememberMe = (state: AuthContextState): boolean => Boolean(state.rememberMeDetails.rememberMe);
const getUserEmail = (state: AuthContextState): string | undefined => state.rememberMeDetails.email;
export {
    AuthContextProvider,
    TestAuthContextProvider,
    useAuthState,
    useAuthHelper,
    isAuthenticated,
    getUserId,
    getRememberMe,
    getUserEmail,
};
